<template>
    <div class="graph-gauge">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import debounce from 'lodash/debounce';
import { Chart, registerables } from 'chart.js';
import { defineComponent } from 'vue';

Chart.register(...registerables);

// Re-usable plugin with mutable state
const centerTextPlugin = {
    id: 'centerText',
    afterDraw(chart) {
        const state = chart?.options?.plugins?.centerText?.state;
        if (!state || state.hidePerc) return;
        const { ctx, width, height } = chart;
        ctx.save();
        const fontSize = (height / 114).toFixed(2);
        ctx.font = `${fontSize}em "Plus Jakarta Sans", sans-serif`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#0D1B2A';
        const text = `${state.value}%`;
        const textX = width / 2;
        const textY = (height / 2) + (fontSize * 10 / 3.5);
        ctx.fillText(text, textX, textY);
        ctx.restore();
    }
};

export default defineComponent({
    name: 'GraphGauge',
    props: {
        value: { type: Number, required: true },
        usage: { type: Boolean, default: false },
        progress: { type: Boolean, default: false },
        hidePerc: { type: Boolean, default: false }
    },
    data() {
        return {
            chart: null,
            debouncedUpdateChart: null,
            isDestroyed: false,
            pauseUpdates: false,
            rafId: null,
            visibilityHandler: null,
            io: null
        };
    },
    methods: {
        clamp(v) {
            if (Number.isNaN(v)) return 0;
            return Math.min(100, Math.max(0, v));
        },
        makeColors(val) {
            if (this.progress) return ['#E9E9ED', '#0075F2'];
            if (this.usage) return ['#CA2E55', '#4AAD52'];
            if (val < 50) return ['#CA2E55', 'rgba(255,255,255,0.36)'];
            if (val < 80) return ['#F7B538', 'rgba(255,255,255,0.36)'];
            return ['#4AAD52', 'rgba(255,255,255,0.36)'];
        },
        createChart() {
            if (this.chart) return; // already created
            this.isDestroyed = false;

            const canvas = this.$refs.canvas;
            if (!canvas) return;
            const ctx = canvas.getContext('2d');

            const val = this.clamp(this.value);

            this.chart = new Chart(ctx, {
                type: 'doughnut',
                data: {
                    datasets: [{
                        data: [val, 100 - val],
                        backgroundColor: this.makeColors(val),
                        borderWidth: 0,
                        hoverOffset: 0
                    }]
                },
                options: {
                    responsive: true,
                    animation: false,
                    animations: { // explicit disable in v3+
                        colors: false,
                        resize: false,
                        active: false
                    },
                    cutout: this.hidePerc ? '50%' : '80%',
                    events: [], // no listeners => less overhead
                    maintainAspectRatio: true,
                    plugins: {
                        tooltip: { enabled: false },
                        legend: { display: false },
                        centerText: {
                            state: {
                                value: val,
                                hidePerc: this.hidePerc
                            }
                        }
                    }
                },
                plugins: [centerTextPlugin]
            });
        },
        scheduleUpdate() {
            if (this.pauseUpdates || this.isDestroyed || !this.chart) return;
            if (this.rafId) return; // already scheduled
            this.rafId = requestAnimationFrame(() => {
                this.rafId = null;
                this.safeUpdate();
            });
        },
        safeUpdate() {
            if (!this.chart || this.isDestroyed) return;
            try {
                this.chart.update('none'); // no animation
            } catch (e) {
                // fallback: rebuild cleanly
                this.destroyChart();
                this.createChart();
            }
        },
        updateValue(newVal) {
            if (!this.chart || this.isDestroyed) return;
            const val = this.clamp(newVal);
            const ds = this.chart.data.datasets[0];
            ds.data[0] = val;
            ds.data[1] = 100 - val;
            ds.backgroundColor = this.makeColors(val);
            // update plugin state
            this.chart.options.plugins.centerText.state.value = val;
            this.chart.options.plugins.centerText.state.hidePerc = this.hidePerc;
            this.scheduleUpdate();
        },
        updateForPropChanges() {
            if (!this.chart || this.isDestroyed) return;
            // Recompute colors / cutout when usage/progress/hidePerc changes
            const val = this.clamp(this.value);
            const ds = this.chart.data.datasets[0];
            ds.backgroundColor = this.makeColors(val);
            this.chart.options.cutout = this.hidePerc ? '50%' : '80%';
            this.chart.options.plugins.centerText.state.hidePerc = this.hidePerc;
            this.scheduleUpdate();
        },
        destroyChart() {
            if (this.isDestroyed) return;
            this.isDestroyed = true;
            if (this.rafId) {
                cancelAnimationFrame(this.rafId);
                this.rafId = null;
            }
            if (this.debouncedUpdateChart?.cancel) {
                this.debouncedUpdateChart.cancel();
            }
            const canvas = this.$refs.canvas;
            if (canvas) {
                const existing = Chart.getChart(canvas);
                if (existing) {
                    try {
                        existing.destroy();
                    } catch (e) {
                        console.error('Error destroying chart:', e);
                    }
                }
                const ctx = canvas.getContext('2d');
                if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
            }
            this.chart = null;
        },
        setupVisibilityHandling() {
            this.visibilityHandler = () => {
                this.pauseUpdates = document.hidden;
            };
            document.addEventListener('visibilitychange', this.visibilityHandler, { passive: true });
        },
        setupIntersectionObserver() {
            // Optional: skip if not needed
            const el = this.$el;
            if (!('IntersectionObserver' in window) || !el) return;
            this.io = new IntersectionObserver((entries) => {
                this.pauseUpdates = !entries[0].isIntersecting;
            }, { threshold: 0.1 });
            this.io.observe(el);
        }
    },
    watch: {
        value(newVal) {
            this.debouncedUpdateChart(() => this.updateValue(newVal));
        },
        usage() { this.updateForPropChanges(); },
        progress() { this.updateForPropChanges(); },
        hidePerc() { this.updateForPropChanges(); }
    },
    created() {
        // Debounce wrapper that still lets us batch multiple prop changes
        this.debouncedUpdateChart = debounce((fn) => fn(), 120, { maxWait: 200 });
    },
    mounted() {
        this.createChart();
        this.setupVisibilityHandling();
        this.setupIntersectionObserver();
    },
    beforeUnmount() {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        if (this.io) {
            try {
                this.io.disconnect();
            } catch (e) {
                console.error('Error disconnecting IntersectionObserver:', e);
            }
            this.io = null;
        }
        this.destroyChart();
    }
});
</script>

<style scoped>
canvas {
    width: 100% !important;
    height: 100% !important;
}
</style>